import axios, { GenericAbortSignal } from 'axios'
import servicesConfig, { CustomAxiosHeaders } from '../config/services'

import { AxiosResponse } from 'axios'
import { NotificationStatusType } from '../util/notification/notification'
import defaults from '@/config'
import { requests } from '.'

// Helper function to handle response
const responseBody = (res: AxiosResponse) => res.data

// Helper function to create a service
const createService = (serviceName: string, host: string) => {
  const opts: CustomAxiosHeaders = { host, serviceName, useNewToken: true }
  return (addlOpts?: CustomAxiosHeaders) => {
    return requests(Object.assign({}, opts, addlOpts))
  }
}

// Helper for filtering query
const getFilteredQuery = (query: { [key: string]: any }) => {
  return Object.fromEntries(Object.entries(query).filter(([, v]) => !!v))
}

// EmailReportingService
const emailReportingRequestCreator = createService('EmailReportingService', servicesConfig.EmailReporting as string)

const EmailReportingService = {
  emailReportingOldUrl: defaults.emailReportingUrl,

  getEmailStatsOld: async (query: { [key: string]: any }) => {
    let records = []
    let total = 0

    const stats = await axios.post(
      `${EmailReportingService.emailReportingOldUrl}/agent_reporting/email_stats_report/bulk_request`,
      {
        location_id: query.locationId,
        option: query.status === 'permanentFail' ? 'permanent_fail' : query.status,
        showCount: query.includeTotalCount,
        maxRows: query.limit,
        start: query.skip,
        bulk_req_id: query.sourceId,
      }
    )
    records = stats.data.response.records
    total = query.includeTotalCount ? stats.data.response.total : 0

    return { records, total }
  },

  getEmailStatsNew: async (query: { [key: string]: any }) => {
    let records = []
    let total = 0

    const stats = await emailReportingRequestCreator()
      .get(`${query ? `?${new URLSearchParams(query).toString()}` : ''}`)
      .then(responseBody)
    records = stats.results.map((r: any) => ({ ...r, email: r.email?.[0] }))
    total = query.includeTotalCount ? stats.total : 0

    return { records, total }
  },

  getEmailExportStats: async (query: { [key: string]: any }) => {
    let results = []
    let total = 0

    const stats = await emailReportingRequestCreator()
      .get(`${query ? `/export?${new URLSearchParams(query).toString()}` : ''}`)
      .then(responseBody)
    results = stats.results
    total = stats.total

    return { results, total }
  },

  getAggregateStatusData: async (query: { [key: string]: any }, useNewDataSource = true) => {
    if (useNewDataSource) {
      const response = await emailReportingRequestCreator()
        .get(`/aggregate${query ? `?${new URLSearchParams(query).toString()}` : ''}`)
        .then(responseBody)
      let results = response.results
      results = Object.keys(results).reduce((acc, curr) => ({ ...acc, [curr]: results[curr].value }), {})
      results['count'] = response.total
      return [results]
    }

    const response = await axios.post(
      `${EmailReportingService.emailReportingOldUrl}/agent_reporting/email_stats/bulk_request/`,
      {
        location_id: query.locationId,
        bulk_req_id: query.sourceId,
      }
    )
    return response.data.response
  },

  getAggregateUsersStats: (body: { [key: string]: any }) =>
    emailReportingRequestCreator().post(`/aggregate/get-users-stats`, body).then(responseBody),
}

// CallsReportingService
const callsReportingRequestCreator = createService('CallsReportingService', servicesConfig.CallsReporting as string)

const CallsReportingService = {
  getCallMetrics: (body: any) => callsReportingRequestCreator().post('/get-call-metrics', body).then(responseBody),

  getAllPhoneCallsNew: (body: any) =>
    callsReportingRequestCreator().post('/get-all-phone-calls-new', body).then(responseBody),
}

// SMSReportingService
const smsReportingRequestCreator = createService('SMSReportingService', servicesConfig.SMSReporting as string)

const SMSReportingService = {
  emailReportingOldUrl: defaults.emailReportingUrl,

  getAggregateUsersStats: (body: { [key: string]: any }) =>
    smsReportingRequestCreator().post(`/aggregate/get-users-stats`, body).then(responseBody),

  getSmsExportStats: async (query: { [key: string]: any }) => {
    let results = []
    let total = 0

    const stats = await smsReportingRequestCreator()
      .get(`${query ? `/export?${new URLSearchParams(query).toString()}` : ''}`)
      .then(responseBody)
    results = stats.results
    total = stats.total

    return { results, total }
  },

  getAggregateStatusData: async (query: { [key: string]: any }, useNewDataSource = true) => {
    if (useNewDataSource) {
      const response = await smsReportingRequestCreator()
        .get(`/aggregate${query ? `?${new URLSearchParams(query).toString()}` : ''}`)
        .then(responseBody)
      let results = response.results
      results = Object.keys(results).reduce((acc, curr) => ({ ...acc, [curr]: results[curr].value }), {})
      results['count'] = response.total
      return [results]
    }

    const response = await axios.post(
      `${SMSReportingService.emailReportingOldUrl}/agent_reporting/sms_stats/bulk_request/`,
      {
        location_id: query.locationId,
        bulk_req_id: query.sourceId,
        user_id: query.userId,
      }
    )
    return response.data.response
  },

  getStatsErrorCodes: async (query: { [key: string]: any }, useNewDataSource = true) => {
    if (useNewDataSource) {
      const response = await smsReportingRequestCreator()
        .get(`/stats-error-codes${query ? `?${new URLSearchParams(query).toString()}` : ''}`)
        .then(responseBody)
      return response
    }

    const response = await axios.post(
      `${SMSReportingService.emailReportingOldUrl}/agent_reporting/sms_stats_errorcodes`,
      {
        location_id: query.locationId,
        bulk_req_id: query.sourceId,
      }
    )
    return response.data.response
  },

  getSMSOld: async (query: { [key: string]: any }) => {
    let records = []
    let total = 0

    let path = `${SMSReportingService.emailReportingOldUrl}/agent_reporting/sms_stats_report`
    const payload: any = {
      location_id: query.locationId,
      pagination: query.pagination,
    }

    if (query.source === 'bulk_request') {
      path += '/bulk_request'
      payload.bulk_req_id = query.sourceId
    } else {
      payload.campaign_id = query.sourceId
      if (query.subSourceId) {
        payload.campaign_step_id = query.subSourceId
      }
    }

    if (query.status === 'delivered') payload['showDelivered'] = true
    else if (query.status === 'clicked') payload['showClicked'] = true
    else if (query.status === 'failed') payload['showFailed'] = true

    const stats = await axios.post(path, payload)

    records = stats.data.page.pageRecords
    total = stats.data.queryTotalCount

    return { records, total }
  },

  getSMSNew: async (query: { [key: string]: string }) => {
    let records = []
    let total = 0

    delete query.pagination

    const data = await smsReportingRequestCreator()
      .get(`${query ? `?${new URLSearchParams(query).toString()}` : ''}`)
      .then(responseBody)

    records = data.results
    total = data.totalCount

    return { records, total }
  },
}

// OpportunitiesReportingService
const opportunitiesReportingRequestCreator = createService(
  'OpportunitiesReportingService',
  servicesConfig.OpportunitiesReporting as string
)

const OpportunitiesReportingService = {
  opportunitiesUrl: defaults.opportunityReportingService,

  getOpportunitiesStats: async (body: { [key: string]: any }): Promise<any> => {
    return opportunitiesReportingRequestCreator().post(`/opportunity-stats`, body).then(responseBody)
  },

  getLeaderBoardStats: async (body: { [key: string]: any }): Promise<any> => {
    return opportunitiesReportingRequestCreator().post(`/leaderboard-stats`, body).then(responseBody)
  },

  getConversionStats: async (body: { [key: string]: any }): Promise<any> => {
    return opportunitiesReportingRequestCreator().post(`/conversion-stats`, body).then(responseBody)
  },

  getSalesVelocityStats: async (body: { [key: string]: any }): Promise<any> => {
    return opportunitiesReportingRequestCreator().post(`/sales-velocity`, body).then(responseBody)
  },
}

// ReportingNotificationService
const reportingNotificationRequestCreator = createService(
  'ReportingNotificationService',
  servicesConfig.ReportingNotification as string
)

const ReportingNotificationService = {
  getAllNotifications: (
    query: {
      notificationStatus?: NotificationStatusType
      locationId?: string
      companyId: string
    },
    signal?: GenericAbortSignal
  ) => {
    const options = signal ? { signal } : {}

    return reportingNotificationRequestCreator()
      .get(`?${new URLSearchParams(getFilteredQuery(query)).toString() || ''}`, options)
      .then(responseBody)
  },

  getNotificationByConfig: (config: { [key: string]: any }, signal?: GenericAbortSignal) => {
    const options = signal ? { signal } : {}

    const url = `${config.endpoint}?${new URLSearchParams(getFilteredQuery(config.query)).toString() || ''}`
    config.body = config.body || {}
    switch (config.method) {
      case 'POST':
        return reportingNotificationRequestCreator().post(url, config.body, options).then(responseBody)
      case 'GET':
      default:
        return reportingNotificationRequestCreator().get(url, options).then(responseBody)
    }
  },

  markNotificationAsViewed: (notificationId: string, body: { locationId?: string; companyId: string }) => {
    return reportingNotificationRequestCreator().put(`/${notificationId}/user/viewed?`, body).then(responseBody)
  },
}

// GAReportingService
const gaReportingRequestCreator = createService('GAReportingService', servicesConfig.GAReporting as string)

const GAReportingService = {
  getAccounts: (locationId: string) =>
    gaReportingRequestCreator().get(`/accounts?locationId=${locationId}`).then(responseBody),

  getAccountProperties: (accountId: string, locationId: string) =>
    gaReportingRequestCreator().get(`/accounts/${accountId}/properties?locationId=${locationId}`).then(responseBody),
}

export const emailReportingService = EmailReportingService
export const callReportingService = CallsReportingService
export const smsReportingService = SMSReportingService
export const opportunitiesReportingService = OpportunitiesReportingService
export const reportingNotificationService = ReportingNotificationService
export const gaReportingService = GAReportingService
